import { useEffect } from 'react';

import { getMixpanelDistinctId } from '@jane/bloom/services/tracking/mixpanel';
import { getCartScriptElement } from '@jane/bloom/utils/getCartScriptElement';
import type { CheckoutViewModel } from '@jane/bloom/viewModels/checkout.server';

import { useHandleCartEvents } from './useHandleCartEvents';

interface UseInitializeCartArgs {
  checkoutData: CheckoutViewModel;
}

export const useInitializeCart = ({ checkoutData }: UseInitializeCartArgs) => {
  const { handleCartEvents, loaded } = useHandleCartEvents(
    checkoutData.payload.storeId
  );

  useEffect(() => {
    window.addEventListener('message', handleCartEvents);

    return () => window.removeEventListener('message', handleCartEvents);
  }, [handleCartEvents]);

  useEffect(() => {
    if (
      /**
       * Scenario 1: script loads for the first time on the checkout page, and the window receives the init message.
       */
      loaded ||
      /**
       * Scenario 2: script loaded on a previous page and the iframe has already initialized and has been added to the DOM.
       * You arrive at checkout, and Remix has kept the iframe in the DOM, but the init message has already fired once
       * and will not fire again.
       */
      getCartScriptElement()
    ) {
      // Resolved client-side (the server-built viewModel can't read Mixpanel).
      const mixpanelDistinctId = getMixpanelDistinctId();
      const message = mixpanelDistinctId
        ? {
            ...checkoutData,
            payload: { ...checkoutData.payload, mixpanelDistinctId },
          }
        : checkoutData;

      getCartScriptElement()?.contentWindow?.postMessage(message, '*');
    }
  }, [loaded, checkoutData]);
};
